import { httpsCallable } from 'firebase/functions';
import {
  SetPspSecretInputSchema,
  TestPspConnectionInputSchema,
  type PspSecretStatus,
  type SetPspSecretInput,
  type TestPspConnectionInput,
} from '@etiquetachef/types';
import { functions } from './firebase';
import { reportNonFatal, serializeError } from './errorReport';
import { assertOnline } from '@etiquetachef/ui-shared/online';

/**
 * Callables das credenciais de PSP (`/psp`): status, gravar chave e testar
 * conexão. Backend: `apps/functions/src/admin/pspSecrets.ts`.
 *
 * Origem: #756 — 2026-10-01 13:31 +0200.
 *
 * Por quê: módulo próprio, fora de `callables.ts`, para não disputar aquele
 * arquivo (muito editado em paralelo). Mesma regra do wrapper de lá: valida o
 * input com o MESMO schema Zod do servidor antes de chamar, e não loga o input
 * — aqui ele carrega a chave.
 *
 * Usado por: `features/psp/PspEditorPage.tsx`.
 */


/**
 * Chama a callable de PSP e, se falhar, reporta ao PostHog antes de relançar.
 *
 * Origem: #1021 — 2026-10-23 (auditoria de erros invisíveis).
 *
 * Por quê: estas callables moravam fora do wrapper genérico de `callables.ts` e a tela
 * (`PspEditorPage`) só mostrava toast — gravar a chave do gateway de pagamento podia falhar sem
 * rastro nenhum em produção. Sem DevTools do cliente isso é invisível.
 *
 * Contrato: `run` faz a chamada; o erro é relançado inalterado. O report leva só o nome da callable
 * e o `code`/mensagem do erro — NUNCA o input, que em `adminSetPspSecret` carrega a chave em claro.
 *
 * Usado por: as três callables deste arquivo.
 */
async function reporting<T>(name: string, run: () => Promise<T>): Promise<T> {
  try {
    return await run();
  } catch (err) {
    reportNonFatal('callable_failed', {
      callable: name,
      code: (err as { code?: string }).code ?? null,
      ...serializeError(err),
    });
    throw err;
  }
}

/** Resultado de `adminTestPspConnection`. */
export type PspConnectionResult = { ok: boolean; account: string | null; message: string | null };

/**
 * Status de cada slot de credencial (provider × modo × tipo), nunca o valor.
 *
 * Origem: #756 — commit f53a7df42, 2026-10-01.
 *
 * Contrato: sem input; devolve `PspSecretStatus[]` (resposta não passa por
 * Zod aqui — o tipo é confiado ao servidor). Rejeita com `FirebaseError` se
 * não for super-admin.
 *
 * Usado por: `features/psp/PspEditorPage.tsx`.
 */
export async function adminGetPspSecretStatusCallable(): Promise<PspSecretStatus[]> {
  assertOnline();
  const fn = httpsCallable<undefined, PspSecretStatus[]>(functions, 'adminGetPspSecretStatus');
  return reporting('adminGetPspSecretStatus', async () => (await fn(undefined)).data);
}

/**
 * Grava uma credencial de PSP no Secret Manager (sem redeploy).
 *
 * Origem: #756 — commit f53a7df42, 2026-10-01.
 *
 * Contrato: valida com `SetPspSecretInputSchema` ANTES de chamar (lança
 * `ZodError` sem ir à rede). Devolve o status novo do slot.
 *
 * Usado por: `features/psp/PspEditorPage.tsx`.
 *
 * Dados — input da callable `adminSetPspSecret`:
 *   { "provider": "asaas", "mode": "sandbox", "kind": "api_key",
 *     "value": "<segredo>", "reason": "rotação" }
 *
 * Dicas: nunca logar `input` — `value` é a chave em claro.
 */
export async function adminSetPspSecretCallable(input: SetPspSecretInput): Promise<PspSecretStatus> {
  assertOnline();
  const fn = httpsCallable<SetPspSecretInput, PspSecretStatus>(functions, 'adminSetPspSecret');
  const parsed = SetPspSecretInputSchema.parse(input);
  return reporting('adminSetPspSecret', async () => (await fn(parsed)).data);
}

/**
 * Testa a conexão com o PSP usando a credencial gravada para o modo.
 *
 * Origem: #756 — commit f53a7df42, 2026-10-01.
 *
 * Contrato: input `{ provider, mode }` validado por
 * `TestPspConnectionInputSchema`; devolve `{ ok, account, message }`.
 *
 * Usado por: `features/psp/PspEditorPage.tsx`.
 */
export async function adminTestPspConnectionCallable(input: TestPspConnectionInput): Promise<PspConnectionResult> {
  assertOnline();
  const fn = httpsCallable<TestPspConnectionInput, PspConnectionResult>(functions, 'adminTestPspConnection');
  const parsed = TestPspConnectionInputSchema.parse(input);
  return reporting('adminTestPspConnection', async () => (await fn(parsed)).data);
}
